import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary. Prevents the entire app from blanking out when a
 * provider (e.g. TooltipProvider) or any descendant throws during render.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="min-h-screen flex items-center justify-center px-6 bg-background text-foreground">
          <div className="max-w-md text-center">
            <p className="text-[11px] tracking-[0.32em] uppercase text-muted-foreground font-sans mb-4">
              Something went sideways
            </p>
            <h1 className="font-serif text-3xl md:text-4xl mb-4">
              A small interruption in the page
            </h1>
            <p className="font-sans text-muted-foreground mb-7 leading-relaxed">
              We hit an unexpected error while loading this view. Refreshing
              usually restores it.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              className="font-sans text-sm bg-primary text-primary-foreground px-5 py-3 rounded-md hover:bg-primary/90 transition-colors"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
